import { useCallback, useEffect, useRef, useState } from 'react';
import { loadChatSearch, type ChatSearchResponse } from './viewers-api';

const STORAGE_KEY = 'ffscontrol.privateChatChime.enabled';
const POLL_INTERVAL_MS = 2_000;

const KNOWN_BOT_LOGINS = new Set([
  'ffscontrol',
  'ffshuntbot',
  'pokemoncommunitygame',
  'tangiabot',
  'nightbot',
  'streamelements',
  'streamlabs',
  'moobot',
  'fossabot',
  'sery_bot',
  'soundalerts',
  'wizebot',
  'botrixoficial',
  'commanderroot',
  'coebot',
  'deepbot',
  'phantombot',
]);

type ChatMessage = ChatSearchResponse['messages'][number];

function storedEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function isPrivateChatChimeEligible(message: ChatMessage): boolean {
  const login = message.user_login.trim().toLowerCase();
  if (!login || KNOWN_BOT_LOGINS.has(login)) return false;
  if (message.message_text.trimStart().startsWith('!')) return false;
  return true;
}

function rememberMessages(messages: ChatMessage[], seen: Set<string>, order: string[]): void {
  for (const message of messages) {
    if (seen.has(message.message_id)) continue;
    seen.add(message.message_id);
    order.push(message.message_id);
  }

  while (order.length > 250) {
    const removed = order.shift();
    if (removed) seen.delete(removed);
  }
}

export function usePrivateChatChime(active: boolean) {
  const [enabled, setEnabled] = useState(storedEnabled);
  const audioContext = useRef<AudioContext | null>(null);
  const pollInFlight = useRef(false);
  const primed = useRef(false);
  const seenIds = useRef(new Set<string>());
  const seenOrder = useRef<string[]>([]);

  const ensureAudio = useCallback(async () => {
    try {
      if (audioContext.current?.state === 'closed') audioContext.current = null;
      const context = audioContext.current ?? new AudioContext();
      audioContext.current = context;
      if (context.state === 'suspended') await context.resume();
      return context.state === 'running' ? context : null;
    } catch {
      return null;
    }
  }, []);

  const playChime = useCallback(async () => {
    const context = await ensureAudio();
    if (!context) return;

    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(920, now);
    oscillator.frequency.exponentialRampToValueAtTime(760, now + 0.085);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.045, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.1);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.105);
  }, [ensureAudio]);

  const resetCursor = useCallback(() => {
    primed.current = false;
    seenIds.current.clear();
    seenOrder.current = [];
  }, []);

  const poll = useCallback(async () => {
    if (!active || !enabled || pollInFlight.current) return;
    pollInFlight.current = true;

    try {
      const body = await loadChatSearch('', '');
      const messages = Array.isArray(body.messages) ? body.messages : [];

      if (!primed.current) {
        rememberMessages(messages, seenIds.current, seenOrder.current);
        primed.current = true;
        return;
      }

      const newMessages = messages.filter((message) => !seenIds.current.has(message.message_id));
      rememberMessages(messages, seenIds.current, seenOrder.current);
      if (newMessages.some(isPrivateChatChimeEligible)) await playChime();
    } catch {
      // Best-effort local notification only; chat polling must never disturb dashboard operation.
    } finally {
      pollInFlight.current = false;
    }
  }, [active, enabled, playChime]);

  useEffect(() => {
    if (!active || !enabled) return;
    const unlock = () => { void ensureAudio(); };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [active, enabled, ensureAudio]);

  useEffect(() => {
    resetCursor();
    if (!active || !enabled) return;
    void poll();
    const timer = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [active, enabled, poll, resetCursor]);

  useEffect(
    () => () => {
      const context = audioContext.current;
      audioContext.current = null;
      if (context && context.state !== 'closed') void context.close();
    },
    [],
  );

  const toggle = useCallback(async () => {
    const next = !enabled;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Local persistence is a convenience only.
    }
    if (next && active) await ensureAudio();
    resetCursor();
    setEnabled(next);
  }, [active, enabled, ensureAudio, resetCursor]);

  return { enabled, toggle };
}
